import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";
import type { TypeButtonSkeleton } from "./TypeButton";

/**
 * Fields type definition for content type 'TypeContent_full'
 * @name TypeContent_fullFields
 * @type {TypeContent_fullFields}
 * @memberof TypeContent_full
 */
export interface TypeContent_fullFields {
    /**
     * Field type definition for field 'name' (Name)
     * @name Name
     * @localized false
     */
    name: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'image' (Image)
     * @name Image
     * @localized false
     */
    image?: EntryFieldTypes.AssetLink;
    /**
     * Field type definition for field 'image_config' (Image Config)
     * @name Image Config
     * @localized false
     */
    image_config?: EntryFieldTypes.Object;
    /**
     * Field type definition for field 'image_aspect_ratio' (Image Aspect Ratio)
     * @name Image Aspect Ratio
     * @localized false
     */
    image_aspect_ratio?: EntryFieldTypes.Symbol<"1/1" | "12/5" | "16/9" | "2/1" | "3/2" | "4/3" | "5/4">;
    /**
     * Field type definition for field 'image_position' (Desktop Image Position)
     * @name Desktop Image Position
     * @localized false
     */
    image_position?: EntryFieldTypes.Symbol<"Bottom" | "Left" | "Right" | "Top">;
    /**
     * Field type definition for field 'image_position_mobile' (Mobile Image Position)
     * @name Mobile Image Position
     * @localized false
     */
    image_position_mobile?: EntryFieldTypes.Symbol<"Bottom" | "Left" | "Right" | "Top">;
    /**
     * Field type definition for field 'eyebrow' (Eyebrow)
     * @name Eyebrow
     * @localized false
     */
    eyebrow?: EntryFieldTypes.RichText;
    /**
     * Field type definition for field 'eyebrow_color' (Eyebrow Color)
     * @name Eyebrow Color
     * @localized false
     */
    eyebrow_color?: EntryFieldTypes.Symbol<"--text-neutral-dark" | "--text-neutral-inverse-dark" | "--text-neutral-inverse-light" | "--text-neutral-light" | "--text-neutral-mid" | "--text-primary-1" | "--text-primary-2" | "--text-primary-3" | "--text-primary-4" | "--text-primary-5" | "--text-primary-6">;
    /**
     * Field type definition for field 'heading' (Heading)
     * @name Heading
     * @localized false
     */
    heading?: EntryFieldTypes.RichText;
    /**
     * Field type definition for field 'heading_color' (Heading Color)
     * @name Heading Color
     * @localized false
     */
    heading_color?: EntryFieldTypes.Symbol<"--text-neutral-dark" | "--text-neutral-inverse-dark" | "--text-neutral-inverse-light" | "--text-neutral-light" | "--text-neutral-mid" | "--text-primary-1" | "--text-primary-2" | "--text-primary-3" | "--text-primary-4" | "--text-primary-5" | "--text-primary-6">;
    /**
     * Field type definition for field 'subhead' (Subhead)
     * @name Subhead
     * @localized false
     */
    subhead?: EntryFieldTypes.RichText;
    /**
     * Field type definition for field 'subhead_color' (Subhead Color)
     * @name Subhead Color
     * @localized false
     */
    subhead_color?: EntryFieldTypes.Symbol<"--text-neutral-dark" | "--text-neutral-inverse-dark" | "--text-neutral-inverse-light" | "--text-neutral-light" | "--text-neutral-mid" | "--text-primary-1" | "--text-primary-2" | "--text-primary-3" | "--text-primary-4" | "--text-primary-5" | "--text-primary-6">;
    /**
     * Field type definition for field 'body' (Body)
     * @name Body
     * @localized false
     */
    body?: EntryFieldTypes.RichText;
    /**
     * Field type definition for field 'body_color' (Body Color)
     * @name Body Color
     * @localized false
     */
    body_color?: EntryFieldTypes.Symbol<"--text-neutral-dark" | "--text-neutral-inverse-dark" | "--text-neutral-inverse-light" | "--text-neutral-light" | "--text-neutral-mid" | "--text-primary-1" | "--text-primary-2" | "--text-primary-3" | "--text-primary-4" | "--text-primary-5" | "--text-primary-6">;
    /**
     * Field type definition for field 'buttons' (Buttons)
     * @name Buttons
     * @localized false
     */
    buttons?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeButtonSkeleton>>;
}

/**
 * Entry skeleton type definition for content type 'content_full' (Content Full)
 * @name TypeContent_fullSkeleton
 * @type {TypeContent_fullSkeleton}
 * @author 4m1b9L6VfuUe5HiWpYWSiQ
 * @since 2024-07-10T16:11:35.390Z
 * @version 29
 */
export type TypeContent_fullSkeleton = EntrySkeletonType<TypeContent_fullFields, "content_full">;
/**
 * Entry type definition for content type 'content_full' (Content Full)
 * @name TypeContent_full
 * @type {TypeContent_full}
 * @author 4m1b9L6VfuUe5HiWpYWSiQ
 * @since 2024-07-10T16:11:35.390Z
 * @version 29
 */
export type TypeContent_full<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeContent_fullSkeleton, Modifiers, Locales>;

export function isTypeContent_full<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeContent_full<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'content_full'
}

export type TypeContent_fullWithoutLinkResolutionResponse = TypeContent_full<"WITHOUT_LINK_RESOLUTION">;
export type TypeContent_fullWithoutUnresolvableLinksResponse = TypeContent_full<"WITHOUT_UNRESOLVABLE_LINKS">;
export type TypeContent_fullWithAllLocalesResponse<Locales extends LocaleCode = LocaleCode> = TypeContent_full<"WITH_ALL_LOCALES", Locales>;
export type TypeContent_fullWithAllLocalesAndWithoutLinkResolutionResponse<Locales extends LocaleCode = LocaleCode> = TypeContent_full<"WITHOUT_LINK_RESOLUTION" | "WITH_ALL_LOCALES", Locales>;
export type TypeContent_fullWithAllLocalesAndWithoutUnresolvableLinksResponse<Locales extends LocaleCode = LocaleCode> = TypeContent_full<"WITHOUT_UNRESOLVABLE_LINKS" | "WITH_ALL_LOCALES", Locales>;
